import { createContext, useContext, useEffect, useRef } from 'react';
import { usePermission } from 'react-permission-role';
import { useStore } from '../../store';
import { useGetCurrentMember } from '../members/members.queries';

interface WorkspacePermissionContextValue {
  isMemberLoading: boolean;
  isMemberError: boolean;
}

const WorkspacePermissionContext = createContext<WorkspacePermissionContextValue>({
  isMemberLoading: true,
  isMemberError: false,
});

export const useWorkspacePermission = () => useContext(WorkspacePermissionContext);

interface WorkspacePermissionProviderProps {
  children: React.ReactNode;
}

/**
 * Provider for managing permissions in the context of the current workspace.
 * Automatically updates permissions when the workspace changes or user roles are modified.
 */
export function WorkspacePermissionProvider({ children }: WorkspacePermissionProviderProps) {
  const { data: member, isLoading: isMemberLoading, error: memberError } = useGetCurrentMember();
  const selectedWorkspaceId = useStore((state) => state.selectedWorkspaceId);
  const user = useStore((state) => state.user);
  const { setUser: setPermissionUser } = usePermission();
  const prevRolesRef = useRef<string[]>([]);
  const hasSetInitialRoles = useRef(false);

  useEffect(() => {
    console.log('WorkspacePermissionProvider state:', {
      member,
      isMemberLoading,
      memberError,
      selectedWorkspaceId,
      user,
      memberRoles: member?.roles,
    });

    // During loading, don't set any roles yet - let consumers handle loading state
    if (isMemberLoading) {
      console.log('Loading state - waiting for member data');
      return;
    }

    // If error fetching member, set empty roles (will be handled by error UI)
    if (memberError) {
      console.log('Error fetching member:', memberError);
      setPermissionUser({
        id: user?.id || null,
        roles: [],
        permissions: [],
      });
      return;
    }

    // If no member or workspace - reset permissions
    if (!member || !selectedWorkspaceId) {
      console.log('No member or workspace data, resetting permissions');
      setPermissionUser({
        id: user?.id || null,
        roles: [],
        permissions: [],
      });
      return;
    }

    // Check if roles have changed
    const currentRoles = member.roles || ['USER'];
    const prevRoles = prevRolesRef.current;
    const rolesChanged = JSON.stringify(currentRoles) !== JSON.stringify(prevRoles);

    if (rolesChanged || !hasSetInitialRoles.current) {
      console.log('Roles changed from', prevRoles, 'to', currentRoles);
      prevRolesRef.current = currentRoles;
      hasSetInitialRoles.current = true;

      // Set permissions based on current workspace roles
      console.log('Setting permissions with roles:', currentRoles);
      setPermissionUser({
        id: user?.id || null,
        roles: currentRoles,
        permissions: [],
      });
    }
  }, [member, isMemberLoading, memberError, selectedWorkspaceId, setPermissionUser, user]);

  return (
    <WorkspacePermissionContext.Provider value={{ isMemberLoading, isMemberError: !!memberError }}>
      {children}
    </WorkspacePermissionContext.Provider>
  );
}
